import { currentOwnerKey } from "@/api/session";
import { useOnline } from "@/app/online";
import { type ReloginSecret, vaultStatus } from "@/unlock";
import { LockKeyhole, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import { PinVerify } from "./PinVerify";

type Stage = "pin" | "confirm-forget" | "wiped";

export function UnlockOverlay({
  onUnlocked,
  onDefer,
  onGiveUp,
}: {
  onUnlocked: (secret: ReloginSecret) => void;
  onDefer: () => void;
  onGiveUp: () => void;
}) {
  const online = useOnline();
  const [stage, setStage] = useState<Stage>("pin");
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void vaultStatus(currentOwnerKey()).then((status) => {
      if (alive) {
        setLabel(status.label);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <dialog className="overlay" open aria-label="Desbloquear con tu PIN">
      <div className="overlay__panel">
        {stage === "wiped" ? (
          <>
            <LockKeyhole size={28} aria-hidden="true" />
            <h2 className="t-h3">Se acabaron los intentos</h2>
            <p className="t-small">
              Borramos la contraseña que estaba guardada en este teléfono. Nada más se perdió: entrá
              con tu usuario y contraseña, y después podés poner un PIN nuevo.
            </p>
            <button type="button" className="btn btn--primary btn--block" onClick={onGiveUp}>
              Entrar con mi contraseña
            </button>
          </>
        ) : null}

        {stage === "confirm-forget" ? (
          <>
            <h2 className="t-h3">¿Borramos el PIN?</h2>
            <p className="t-small">
              Se borra la contraseña guardada en este teléfono y entrás escribiéndola. Después podés
              poner un PIN nuevo.
            </p>
            <button type="button" className="btn btn--primary btn--block" onClick={onGiveUp}>
              Sí, entrar con mi contraseña
            </button>
            <button type="button" className="btn btn--block" onClick={() => setStage("pin")}>
              Volver al PIN
            </button>
          </>
        ) : null}

        {stage === "pin" ? (
          <>
            <LockKeyhole size={28} aria-hidden="true" />
            <p className="t-eyebrow">Tu sesión venció</p>
            {online ? null : (
              <p className="notice notice--offline">
                <WifiOff size={16} aria-hidden="true" />
                El PIN se controla en el teléfono, pero renovar la sesión necesita señal. Lo que ya
                cargaste queda guardado y sale cuando retomes la sesión con señal.
              </p>
            )}
            <PinVerify
              disabled={!online}
              title="Marcá tu PIN"
              hint={
                label
                  ? `Seguís donde estabas, ${label}. Nada de lo que cargaste se perdió.`
                  : "Seguís donde estabas. Nada de lo que cargaste se perdió."
              }
              onVerified={onUnlocked}
              onWiped={() => setStage("wiped")}
              onMissing={onGiveUp}
              footer={
                <>
                  <button type="button" className="btn btn--block" onClick={onDefer}>
                    Ahora no
                  </button>
                  <button
                    type="button"
                    className="btn btn--block"
                    onClick={() => setStage("confirm-forget")}
                  >
                    Olvidé el PIN
                  </button>
                </>
              }
            />
          </>
        ) : null}
      </div>
    </dialog>
  );
}
